import { useState } from 'react';
import { money, utcDate } from '../format';
import type { TeamSummary, UnmanagedKeyOut } from '../types';
import { ClaimKeyForm } from './ClaimKeyForm';

interface UnmanagedKeyListProps {
  items: UnmanagedKeyOut[];
  teams: TeamSummary[];
  onAfterClaim: () => Promise<void>;
}

export function UnmanagedKeyList({ items, teams, onAfterClaim }: UnmanagedKeyListProps) {
  const [claimingId, setClaimingId] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className="empty">
        <p>No pending unmanaged keys. All upstream keys are claimed or ignored.</p>
      </div>
    );
  }

  return (
    <div>
      <table className="keys">
        <caption className="sr-only">Unmanaged keys discovered upstream, ready to claim or ignore</caption>
        <thead>
          <tr>
            <th scope="col">Key</th>
            <th scope="col">Status</th>
            <th scope="col">Usage</th>
            <th scope="col">First seen</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((key) => (
            <tr key={key.id} className="row-unmanaged">
              <td>
                <div className="key-name">{key.name}</div>
                {key.label && (
                  <div className="key-label">
                    <span className="mono">{key.label}</span>
                    <span className="muted"> masked preview</span>
                  </div>
                )}
              </td>
              <td>
                <span className="badge badge-unmanaged">{key.status}</span>
                {key.disabled && <span className="badge" style={{ marginLeft: '4px' }}>disabled</span>}
              </td>
              <td className="numeric">{money(key.usage, 'unknown')}</td>
              <td className="numeric">{key.first_seen_at ? utcDate(key.first_seen_at) : 'unknown'}</td>
              <td>
                {claimingId === key.id ? (
                  <ClaimKeyForm
                    unmanagedKey={key}
                    teams={teams}
                    onSubmit={async () => {
                      await onAfterClaim();
                      setClaimingId(null);
                    }}
                    onCancel={() => setClaimingId(null)}
                  />
                ) : (
                  <button
                    onClick={() => setClaimingId(key.id)}
                    className="primary"
                    style={{ width: '100%' }}
                  >
                    Claim
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
